import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { FiEdit2 } from 'react-icons/fi';
import { FaChevronDown } from 'react-icons/fa';
import Modal from '../layouts/Modal';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import useTaskStatuses from '../../hooks/community/useTaskStatuses';
import { taskService } from '../../services/taskService';
import Button from '../misc/Button';
import ParticipantPicker from '../misc/ParticipantPicker';
import DateTimePicker from '../forms/DateTimePicker';
import { Task } from '../../types/task';
import { ManageStatusesModal } from './CreateTaskModal';
import HorizontalSponsorsList from '../misc/HorizontalSponsorsList';

const getStatusLabel = (status: import('../../types/task').TaskStatus, t: (key: string) => string): string => {
  if (status.systemKey === 'OPEN') return t('page.dashboard.tasks.defaultOpenStatus');
  if (status.systemKey === 'CLOSED') return t('page.dashboard.tasks.defaultClosedStatus');
  return status.name;
};

interface EditTaskModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  task: Task | null;
  onSave?: () => void;
  onDelete?: () => Promise<void>;
}

export default function EditTaskModal({ open, setOpen, task, onSave, onDelete }: EditTaskModalProps) {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const { statuses, createStatus, updateStatus, deleteStatus, reload } = useTaskStatuses(community?.id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [assignees, setAssignees] = useState<number[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showManageStatuses, setShowManageStatuses] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    setName(task.name);
    setDescription(task.description ?? '');
    const d = task.dueDate ? new Date(task.dueDate) : undefined;
    setDueDate(d);
    setAssignees(task.assignees?.map((a) => a.userId) || []);
    setSelectedStatusId(task.status?.statusId);
    setErrors({});
    setConfirmDelete(false);
    setShowManageStatuses(false);
  }, [open, task]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('page.createTask.errors.nameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await onDelete!();
    } catch {
      toast.error(t('error.deleteFailed'));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleSave = async () => {
    if (!community?.id || !task?.taskId) return;
    if (!validate()) return;

    setLoading(true);
    try {
      await taskService.updateTask(community.id, task.taskId, {
        name: name.trim(),
        description: description.trim() || undefined,
        dueDate,
        statusId: selectedStatusId ?? null,
      });

      toast.success(t('page.dashboard.tasks.updateSuccess'));
      setOpen(false);
      onSave?.();
    } catch (err) {
      console.error('Failed to update task:', err);
      toast.error(t('error.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        title={<>{t('page.editTask.title') || t('page.createTask.title')}</>}
        footer={
          <div className="mt-4 flex items-center gap-3">
            {onDelete && (
              <Button
                type="button"
                variant="danger"
                disabled={loading || deleting}
                onClick={handleDelete}
                className="mr-auto px-6 py-2">
                {deleting ? t('general.deleting') : confirmDelete ? t('general.confirmDelete') : t('general.delete')}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setConfirmDelete(false);
                setOpen(false);
              }}
              className="ml-auto px-6 py-2">
              {t('general.cancel')}
            </Button>
            <Button type="button" variant="primary" disabled={loading} onClick={handleSave} className="px-6 py-2">
              {loading ? t('page.createTask.saving') : t('general.save')}
            </Button>
          </div>
        }>
        <div className="flex flex-col gap-6">
          {task?.taskId != null && <HorizontalSponsorsList taskId={task.taskId} hideTitle />}

          <div>
            <label htmlFor="task-name" className="mb-1 block text-sm font-medium text-primary">
              {t('page.createTask.name')}
            </label>
            <textarea
              id="task-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: '' });
              }}
              placeholder={t('page.createTask.namePlaceholder')}
              rows={2}
              className={classNames(
                'w-full resize-none rounded-lg border-none !bg-secondary-50 p-2',
                errors.name && 'ring-2 ring-error',
              )}
            />
            {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="task-desc" className="mb-1 block text-sm font-medium text-primary">
              {t('page.createTask.description')}
            </label>
            <textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('page.createTask.descriptionPlaceholder')}
              rows={3}
              className="w-full resize-y rounded-lg border-none !bg-secondary-50 p-2"
            />
          </div>

          <div>
            <label htmlFor="task-due" className="mb-1 block text-sm font-medium text-primary">
              {t('page.createTask.dueDate')}
            </label>
            <DateTimePicker
              value={dueDate}
              onChange={(date) => {
                setDueDate(date ?? undefined);
              }}
              dateOnly
              className={classNames('')}
              ariaLabel={t('page.createTask.dueDate')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">{t('page.createTask.assignees')}</label>
            <ParticipantPicker communityId={community?.id} selected={assignees} setSelected={setAssignees} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">{t('page.dashboard.tasks.status')}</label>
            <StatusPickerInline
              statuses={statuses}
              selectedStatusId={selectedStatusId}
              onSelect={setSelectedStatusId}
              onManage={() => setShowManageStatuses(true)}
            />
          </div>
        </div>
      </Modal>

      <ManageStatusesModal
        open={showManageStatuses}
        setOpen={setShowManageStatuses}
        statuses={statuses}
        onCreate={createStatus}
        onUpdate={(id, data) => updateStatus(id, data)}
        onDelete={deleteStatus}
        onReload={reload}
      />
    </>
  );
}

function StatusPickerInline({
  statuses,
  selectedStatusId,
  onSelect,
  onManage,
}: {
  statuses: import('../../types/task').TaskStatus[];
  selectedStatusId?: number;
  onSelect: (id: number | undefined) => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const selected = statuses.find((s) => s.statusId === selectedStatusId);

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <button
          type="button"
          onClick={() => setDropdownOpen((v) => !v)}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
          className="flex w-full items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary">
          {selected ? (
            <>
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: selected.color }}
              />
              <span className="flex-1 text-left">{getStatusLabel(selected, t)}</span>
            </>
          ) : (
            <span className="flex-1 text-left text-details-500">{t('page.dashboard.tasks.statusPlaceholder')}</span>
          )}
          <FaChevronDown className="h-3 w-3 flex-shrink-0 text-details-500" />
        </button>
        {dropdownOpen && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-secondary-200 bg-white shadow-lg">
            {statuses.map((s) => (
              <button
                key={s.statusId}
                type="button"
                onMouseDown={() => {
                  onSelect(s.statusId);
                  setDropdownOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-secondary-50">
                <span
                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                {getStatusLabel(s, t)}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onManage}
        title={t('page.dashboard.tasks.manageStatuses')}
        className="flex items-center rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary hover:bg-secondary-50">
        <FiEdit2 className="h-4 w-4" />
      </button>
    </div>
  );
}
